import React, { useState, useEffect } from 'react';
import Header from '../../Header/Header.jsx';
import Footer from '../../Footer/Footer.jsx';
import staymojoVideo from '../../../images/staymojo.mp4';
import styles from './Staymojo.module.css';

const StayMojoProjectShowcase = () => {
  const [hoveredTool, setHoveredTool] = useState(null);
  const [hoveredResponsibility, setHoveredResponsibility] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mode, setMode] = useState('web');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const responsibilities = [
    { text: 'Content Structuring & Ideation', icon: '💡', desc: 'Build meaningful sections reflecting experience, ethos, and service offerings' },
    { text: 'UX & UI Guidance', icon: '🎯', desc: 'Structure digital flow to highlight unique stays and value propositions' },
    { text: 'Brand Messaging Optimization', icon: '📌', desc: 'Align hospitality voice with eco-friendly positioning and value messaging' },
    { text: 'Website Content Implementation', icon: '🌐', desc: 'Organize About, Stay Details, Experience Highlights, and Contact info' },
    { text: 'Visual Narrative Planning', icon: '📸', desc: 'Guide imagery, icons, and experiential visual elements for engagement' },
    { text: 'Copy & Content Editing', icon: '✍️', desc: 'Edit homepage and about sections for clarity and conversion intent' },
    { text: 'Continuous Refinement', icon: '🚀', desc: 'Update and iterate content based on user feedback and live analytics' }
  ];

  const tools = [
    { name: 'CMS / Content Tools', icon: '📝', description: 'HTML/PHP blocks' },
    { name: 'Figma', icon: '🎨', description: 'UX flow mockups' },
    { name: 'Photoshop', icon: '🖼️', description: 'Image optimization' },
    { name: 'Illustrator', icon: '✨', description: 'Custom iconography' },
    { name: 'Analytics', icon: '📊', description: 'Engagement tracking' }
  ];

  const skills = [
    'CSS', 'HTML5', 'UX Writing', 'Brand Storytelling', 'Responsive Layouts',
    'Visual Hierarchy', 'User Flow Optimization', 'Content Strategy', 'Photography Editing'
  ];



  return (
    <>
      <Header mode={mode} setMode={setMode} menuOpen={menuOpen} setMenuOpen={setMenuOpen} scrolled={scrolled} />
      <div className={styles.container}>
        {/* Animated Background */}
        <div className={styles.backgroundGradient}>
          <div className={styles.gradientOrb1}></div>
          <div className={styles.gradientOrb2}></div>
          <div className={styles.gradientOrb3}></div>
        </div>

        <div className={styles.contentWrapper}>
          {/* Hero Section */}
          <section className={styles.heroSection}>
            <div className={styles.heroContent}>
              <div className={styles.heroLeft}>
                <div className={styles.projectBadge}>
                  <span className={styles.badgeDot}></span>
                  <span>Ongoing Project</span>
                </div>
                
                <h1 className={styles.projectTitle}>Hotel Booking Platform</h1>
                <p className={styles.projectSubtitle}>Himalayan Roots Authentic Eco-Stay & Mountain Hospitality</p>
                
                <div className={styles.projectMeta}>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Work</span>
                    <span className={styles.metaValue}>Website Content & Branding</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Year</span>
                    <span className={styles.metaValue}>2025</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Duration</span>
                    <span className={styles.metaValue}>Ongoing</span>
                  </div>
                </div>
                
                <p className={styles.heroDescription}>
                    A Hotel Booking Platform designed to deliver eco-friendly, value-driven hospitality experiences with a focus on nature-centric stays. It highlights cozy accommodations and sustainable tourism, offering users a seamless way to explore and book unique stays rooted in authentic local environments.
                </p>
                
                
              </div>
              
              <div className={styles.heroRight}>
                <div className={styles.heroVisual}>
                  <video 
                    className={styles.heroVideo}
                    autoPlay 
                    loop 
                    muted 
                    playsInline
                  >
                    <source src={staymojoVideo} type="video/mp4" />
                  </video>
                </div>
              </div>
            </div>
          </section>

          {/* Introduction Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Introduction</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.contentCard}>
              <p className={styles.contentText}>
              This platform enhances the travel experience by combining comfort, simplicity, and immersion in nature through a digital-first booking system. It showcases thoughtfully designed stays that emphasize sustainability, safety, and affordability while reflecting the essence of local culture and surroundings. The experience is centered around minimal-impact living, warm hospitality, and providing travelers with a peaceful escape connected to nature.
              </p>
            </div>
          </section>

          {/* My Role Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>My Role</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.contentCard}>
              <p className={styles.contentText}>
              My role involved translating the brand’s hospitality vision into a clear and engaging digital experience. I worked on content structuring, UX direction, and aligning messaging to highlight eco-friendly values and stay experiences. Additionally, I implemented the hotel booking system using QloApps, customized platform settings, and configured payment integrations to enable a smooth and reliable end-to-end reservation process.
            </p>
            </div>
          </section>

          {/* Responsibilities Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Responsibilities</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.responsibilitiesGrid}>
              {responsibilities.map((responsibility, index) => (
                <div 
                  key={index}
                  className={styles.responsibilityCard}
                  onMouseEnter={() => setHoveredResponsibility(index)}
                  onMouseLeave={() => setHoveredResponsibility(null)}
                >
                  <span className={styles.cardNumber}>{String(index + 1).padStart(2, '0')}</span>
                  <div className={styles.cardIcon}>{responsibility.icon}</div>
                  <div className={styles.cardText}>{responsibility.text}</div>
                  <div className={styles.cardDesc}>{responsibility.desc}</div>
                </div>
              ))}
            </div>
          </section>

          

          {/* Tools Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Tools Used</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.toolsGrid}>
              {tools.map((tool, index) => (
                <div 
                  key={index}
                  className={styles.toolCard}
                  onMouseEnter={() => setHoveredTool(index)}
                  onMouseLeave={() => setHoveredTool(null)}
                >
                  <div className={styles.toolGlow}></div>
                  <div className={styles.toolIcon}>{tool.icon}</div>
                  <div className={styles.toolName}>{tool.name}</div>
                  <div className={styles.toolDescription}>{tool.description}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Skills Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Technologies & Skills</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.skillsContainer}>
              {skills.map((skill, index) => (
                <div 
                  key={index}
                  className={styles.skillTag}
                >
                  <span className={styles.skillHash}>#</span>
                  {skill}
                </div>
              ))}
            </div>
          </section>

          
        </div>
      </div>
      <div className={styles.footerOverride}>
        <Footer />
      </div>
    </>
  );
};

export default StayMojoProjectShowcase;